import { Head } from "@/components/Head";
import { Section, Eyebrow } from "@/components/Section";

export function LegalPrivacyPage() {
  return (
    <>
      <Head
        title="Privacy Policy — ServiTrex"
        description="Draft privacy policy describing how ServiTrex collects, uses, and protects personal information."
      />
      <Section narrow className="pt-14 sm:pt-16">
        <Eyebrow>Legal</Eyebrow>
        <h1 className="text-4xl sm:text-5xl">Privacy Policy</h1>
        <p className="mt-4 text-sm text-muted">Last updated: July 16, 2026</p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-muted sm:text-base">
          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">1. Overview</h2>
            <p className="mt-3">
              This Privacy Policy explains how ServiTrex, a Cognautic company (“ServiTrex,”
              “we,” “us,” or “our”), collects, uses, discloses, and protects personal
              information in connection with servitrex.com, app.servitrex.com, TrexField,
              and related services (the “Services”).
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">2. Information we collect</h2>
            <p className="mt-3">We collect information in three broad categories:</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>
                <strong className="text-fg">Account & business information</strong> such as
                names, work emails, phone numbers, company details, roles, billing contacts,
                and plan preferences.
              </li>
              <li>
                <strong className="text-fg">Customer-operated data</strong> that our business
                customers upload or generate in the platform, including end-customer contact
                details, job history, recordings or transcripts where enabled, invoices, and
                service notes.
              </li>
              <li>
                <strong className="text-fg">Technical & usage data</strong> such as device
                type, browser, IP address, approximate location, log data, cookie identifiers,
                and product interaction events.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">3. How we use information</h2>
            <p className="mt-3">We use personal information to:</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Provide, secure, maintain, and improve the Services</li>
              <li>Create accounts, authenticate users, and administer tenants</li>
              <li>Process transactions, usage metering, and customer support</li>
              <li>Deliver product communications and, where permitted, marketing</li>
              <li>Detect fraud, abuse, and reliability issues</li>
              <li>Comply with legal obligations and enforce agreements</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">4. AI voice & communications</h2>
            <p className="mt-3">
              If your organization enables AI voice agents, telephony, or messaging,
              call/message metadata and content may be processed to route conversations,
              generate transcripts or summaries, book jobs, and improve reliability. Our
              business customers control when those features are enabled and are responsible
              for providing any required notices or consents to their own end customers and
              employees.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">5. How we share information</h2>
            <p className="mt-3">
              We do not sell personal information. We may share information with:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>Service providers who assist with hosting, analytics, payments, communications, and support under contractual confidentiality obligations</li>
              <li>Affiliates within the Cognautic corporate family for operational purposes</li>
              <li>Professional advisors, regulators, or law enforcement when required by law or to protect rights and safety</li>
              <li>Successor entities in connection with a merger, acquisition, or corporate reorganization</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">6. Customer-controlled processing</h2>
            <p className="mt-3">
              For Customer Data uploaded by a business subscriber, ServiTrex generally acts
              as a service provider / processor. That subscriber determines the purposes of
              processing for its end-customer information and is responsible for its own
              privacy notices, retention choices, and lawful basis.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">7. Cookies & analytics</h2>
            <p className="mt-3">
              We use cookies and similar technologies for authentication, preferences,
              analytics, and performance. You can control cookies through browser settings.
              Some features may not function if essential cookies are disabled.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">8. Data retention</h2>
            <p className="mt-3">
              We retain personal information for as long as needed to provide the Services,
              meet legal and accounting requirements, resolve disputes, and enforce
              agreements. Retention for Customer Data is largely controlled by the business
              customer’s account settings and lifecycle.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">9. Security</h2>
            <p className="mt-3">
              We use access controls, encryption in transit, monitoring, and other safeguards
              designed to protect personal information. No system can be guaranteed fully
              secure. Please use strong unique passwords and protect devices used to access
              ServiTrex.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">10. Your choices & rights</h2>
            <p className="mt-3">
              Depending on your location, you may have rights to access, correct, delete, or
              export personal information, or to object to or restrict certain processing.
              Account users can often update profile details in-product. For other requests,
              contact privacy@servitrex.com. If you are an end customer of a ServiTrex
              subscriber, please contact that business first; we may redirect your request
              to them.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">11. International transfers</h2>
            <p className="mt-3">
              ServiTrex is operated for customers primarily in the United States and Canada.
              Information may be processed in the United States and other locations where we
              or our providers operate, subject to appropriate safeguards.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">12. Children’s privacy</h2>
            <p className="mt-3">
              The Services are designed for business users and are not directed to children
              under 16. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">13. Changes to this policy</h2>
            <p className="mt-3">
              We may update this Privacy Policy periodically. The “Last updated” date will
              change when we do. Material updates may also be communicated through the
              Services or email.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">14. Contact</h2>
            <p className="mt-3">
              Privacy questions: privacy@servitrex.com. General inquiries:
              hello@servitrex.com.
            </p>
          </section>
        </div>

        <p className="mt-12 rounded-2xl border border-border bg-surface-muted px-4 py-3 text-xs text-muted">
          Draft — pending counsel review. This page is a professional working draft for
          product launch preparation and is not final legal advice.
        </p>
      </Section>
    </>
  );
}
